/**
 * MoneyRoom — Mission Control Phase D.
 *
 * Surfaces GMV, agent costs (with period toggle), payout queue,
 * and booking counts.
 *
 * Data: GET /api/admin/ops/money  (polled every 60 s)
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Coins, RefreshCw, TrendingUp, CreditCard, AlertCircle } from "lucide-react";
import { API } from "@/App";
import { getAdminToken } from "@/lib/authStorage";

// ── Auth helper ───────────────────────────────────────────────────────────────

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Formatting helpers ────────────────────────────────────────────────────────

function fmtGmv(value) {
  if (!value) return "—";
  return "$" + Number(value).toLocaleString("en-US");
}

function fmtCost(value) {
  if (value == null) return "—";
  return "$" + Number(value).toFixed(2);
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ── Inline sub-components ─────────────────────────────────────────────────────

function StatCard({ value, label, sublabel, accent }) {
  return (
    <div className={`mc-stat-card${accent ? " mc-stat-card--accent" : ""}`}>
      <span className="mc-stat-value">{value}</span>
      <span className="mc-stat-label">{label}</span>
      {sublabel && <span className="mc-stat-sublabel">{sublabel}</span>}
    </div>
  );
}

function GhostStatCard() {
  return (
    <div
      className="mc-stat-card mc-agent-tile-ghost"
      aria-hidden="true"
      style={{ minHeight: 110 }}
    />
  );
}

function SectionHeader({ title, right }) {
  return (
    <div className="mc-section-header">
      <span className="mc-section-title">{title}</span>
      {right}
    </div>
  );
}

function PeriodToggle({ period, onChange }) {
  const opts = ["24h", "7d", "30d"];
  return (
    <div className="mc-period-toggle" role="group" aria-label="Period">
      {opts.map((p) => (
        <button
          key={p}
          className={`mc-period-btn${period === p ? " mc-period-btn--active" : ""}`}
          onClick={() => onChange(p)}
          aria-pressed={period === p}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MoneyRoom() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [costPeriod, setCostPeriod] = useState("24h");

  const fetchMoney = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/ops/money`, {
        headers: authHeaders(),
      });
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMoney();
    const id = setInterval(fetchMoney, 60_000);
    return () => clearInterval(id);
  }, [fetchMoney]);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const gmv = data?.gmv ?? {};
  const costs = data?.agent_costs ?? {};
  const queue = data?.payout_queue ?? {};
  const bookings = data?.bookings ?? {};

  const costValue =
    costPeriod === "24h"
      ? costs.last_24h
      : costPeriod === "7d"
      ? costs.last_7d
      : costs.last_30d;

  const breakdown = costs.breakdown_24h ?? [];
  const maxCost = breakdown.length
    ? Math.max(...breakdown.map((b) => b.cost_usd))
    : 0;
  const topBreakdown = breakdown.slice(0, 8);

  const payoutItems = queue.items ?? [];

  // ── Loading state ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <section className="mc-room mc-room--money" aria-label="Money room">
        <header className="mc-room-header">
          <div>
            <p className="mc-room-eyebrow">ROOM · MONEY</p>
            <h1 className="mc-room-title">Money</h1>
          </div>
        </header>

        {/* Revenue skeleton */}
        <SectionHeader title="Revenue" />
        <div className="mc-stat-grid">
          <GhostStatCard />
          <GhostStatCard />
          <GhostStatCard />
        </div>

        {/* Costs skeleton */}
        <SectionHeader title="Agent Costs" />
        <div className="mc-stat-grid">
          <GhostStatCard />
          <GhostStatCard />
          <GhostStatCard />
        </div>
      </section>
    );
  }

  // ── Error state (no data yet) ───────────────────────────────────────────────

  if (error && !data) {
    return (
      <section className="mc-room mc-room--money" aria-label="Money room">
        <header className="mc-room-header">
          <div>
            <p className="mc-room-eyebrow">ROOM · MONEY</p>
            <h1 className="mc-room-title">Money</h1>
          </div>
        </header>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            padding: "48px 0",
            color: "var(--color-critical)",
            fontFamily: "var(--font-sans)",
            fontSize: 14,
          }}
        >
          <AlertCircle size={24} />
          <span>Failed to load money data.</span>
          <button
            onClick={fetchMoney}
            style={{
              padding: "6px 16px",
              border: "1px solid var(--color-critical)",
              borderRadius: 4,
              background: "transparent",
              color: "var(--color-critical)",
              cursor: "pointer",
              fontFamily: "var(--font-sans)",
              fontSize: 13,
            }}
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  // ── Nominal render ──────────────────────────────────────────────────────────

  return (
    <section className="mc-room mc-room--money" aria-label="Money room">
      {/* ── Room header ── */}
      <header className="mc-room-header">
        <div>
          <p className="mc-room-eyebrow">ROOM · MONEY</p>
          <h1 className="mc-room-title">Money</h1>
        </div>
        <button
          className="mc-chip mc-chip--neutral"
          onClick={fetchMoney}
          aria-label="Refresh money data"
          style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
        >
          <RefreshCw size={11} />
          refresh
        </button>
      </header>

      {/* ── Revenue ─────────────────────────────────────────────────────────── */}
      <SectionHeader title="Revenue" />
      <div className="mc-stat-grid">
        <StatCard
          value={fmtGmv(gmv.total)}
          label="GMV"
          sublabel="All time"
          accent
        />
        <StatCard
          value={fmtGmv(gmv.last_30d)}
          label="Last 30d"
          sublabel={
            gmv.bookings_completed_30d != null
              ? `${gmv.bookings_completed_30d} bookings`
              : undefined
          }
        />
        <StatCard
          value={fmtGmv(gmv.last_7d)}
          label="Last 7d"
        />
      </div>

      {/* ── Agent Costs ──────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Agent Costs"
        right={
          <PeriodToggle period={costPeriod} onChange={setCostPeriod} />
        }
      />
      <div className="mc-stat-grid">
        <StatCard value={fmtCost(costs.last_24h)} label="Last 24h" accent />
        <StatCard value={fmtCost(costs.last_7d)} label="Last 7d" />
        <StatCard value={fmtCost(costs.last_30d)} label="Last 30d" />
      </div>

      {/* Cost breakdown bars */}
      <div className="mc-cost-bar-wrap" aria-label="Agent cost breakdown (24h)">
        {topBreakdown.length === 0 ? (
          <p className="mc-empty-state" style={{ padding: "16px 0" }}>
            No agent activity in last 24h
          </p>
        ) : (
          topBreakdown.map((entry) => {
            const pct = maxCost > 0 ? (entry.cost_usd / maxCost) * 100 : 0;
            return (
              <div className="mc-cost-bar-row" key={entry.agent_id}>
                <span className="mc-cost-bar-label" title={entry.agent_id}>
                  {entry.agent_id}
                </span>
                <div
                  className="mc-cost-bar-track"
                  role="progressbar"
                  aria-valuenow={entry.cost_usd}
                  aria-valuemax={maxCost}
                >
                  <div
                    className="mc-cost-bar-fill"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="mc-cost-bar-value">{fmtCost(entry.cost_usd)}</span>
              </div>
            );
          })
        )}
      </div>

      {/* ── Payout Queue ─────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Payout Queue"
        right={
          queue.pending_count != null ? (
            <span className="mc-section-count">
              {queue.pending_count} pending · {fmtGmv(queue.total_owed_usd)} owed
            </span>
          ) : null
        }
      />

      {payoutItems.length === 0 ? (
        <div className="mc-empty-state">No payouts pending</div>
      ) : (
        <table className="mc-data-table" aria-label="Payout queue">
          <thead>
            <tr>
              <th>Expert ID</th>
              <th>Amount</th>
              <th>Since</th>
            </tr>
          </thead>
          <tbody>
            {payoutItems.map((item) => {
              const shortId = (item.expert_id ?? item.id ?? "").slice(0, 8);
              return (
                <tr key={item.id}>
                  <td>
                    <span className="mc-mono" style={{ fontSize: 12, color: "var(--color-slate-400)" }}>
                      {shortId}
                    </span>
                  </td>
                  <td>
                    <span
                      className="mc-mono"
                      style={{ fontSize: 13, color: "var(--color-green)", fontWeight: 500 }}
                    >
                      {fmtGmv(item.amount)}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: "var(--color-slate-400)", fontSize: 12 }}>
                      {fmtDate(item.created_at)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* ── Bookings ─────────────────────────────────────────────────────────── */}
      <SectionHeader title="Bookings" />
      <div
        className="mc-stat-grid"
        style={{ gridTemplateColumns: "repeat(2, 1fr)", maxWidth: 480 }}
      >
        <StatCard
          value={bookings.pending ?? "—"}
          label="Pending"
          sublabel="Briefs"
          accent
        />
        <StatCard
          value={bookings.completed_30d ?? "—"}
          label="Completed"
          sublabel="Last 30d"
        />
      </div>
    </section>
  );
}
